import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { formatPrice, productTitle } from '@/lib/format'

import {
  GridEdgeFade,
  GridEmpty,
  GridFrame,
  GridItem,
  GridItemCode,
  GridItemFooter,
  GridItemIcon,
  GridItemImage,
  GridItemPrice,
  GridItemTitle,
  GridScrollArea,
  GridViewport,
  GridWrapper,
  PageButton,
  PageIndicator,
  Pager,
  PagedGrid,
} from './ProductGrid.styles'

// A tap adds the product; a swipe (or a long press) must not
const TAP_MAX_MOVE = 10
const TAP_MAX_MS = 800

const ChevronIcon = ({ up }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d={up ? 'M6 15l6-6 6 6' : 'M6 9l6 6 6-6'} />
  </svg>
)

const ProductGrid = ({ products, currency, onProductClick, filterQuery, activeCategory }) => {
  const { t, i18n } = useTranslation()

  const filtered = useMemo(() => {
    let result = products

    if (activeCategory && activeCategory !== 'All') {
      result = result.filter(p => p.category === activeCategory)
    }

    if (filterQuery) {
      const q = filterQuery.toLowerCase()
      result = result.filter(p =>
        p.id?.toLowerCase().includes(q) ||
        p.title?.toLowerCase().includes(q) ||
        p.titlePl?.toLowerCase().includes(q) ||
        p.category?.toLowerCase().includes(q)
      )
    }

    return result
  }, [products, activeCategory, filterQuery])

  const frameRef = useRef(null)
  const scrollRef = useRef(null)
  const gridRef = useRef(null)
  const pressRef = useRef(null)
  const [geo, setGeo] = useState({ pitch: 0, rows: 1, cols: 1, viewH: 0, pages: 1, pad: 0 })
  const [pos, setPos] = useState({ page: 1, canUp: false, canDown: false })

  const pageHeight = geo.rows * geo.pitch

  const updatePosition = useCallback(() => {
    const el = scrollRef.current
    if (!el || !pageHeight) {
      setPos(prev => (prev.page === 1 && !prev.canUp && !prev.canDown ? prev : { page: 1, canUp: false, canDown: false }))
      return
    }
    const max = el.scrollHeight - el.clientHeight
    const top = el.scrollTop
    const next = {
      page: Math.min(geo.pages, Math.round(top / pageHeight) + 1),
      canUp: top > 2,
      canDown: top < max - 2,
    }
    setPos(prev => (
      prev.page === next.page && prev.canUp === next.canUp && prev.canDown === next.canDown ? prev : next
    ))
  }, [geo.pages, pageHeight])

  // Page = number of fully visible rows; the viewport is sized to whole rows
  const measure = useCallback(() => {
    const frame = frameRef.current
    const grid = gridRef.current
    const card = grid?.querySelector('[data-product-id]')
    if (!frame || !grid || !card) {
      setGeo(prev => (prev.pages === 1 && prev.viewH === 0 ? prev : { pitch: 0, rows: 1, cols: 1, viewH: 0, pages: 1, pad: 0 }))
      return
    }
    const style = getComputedStyle(grid)
    const gap = parseFloat(style.rowGap) || 0
    const cols = style.gridTemplateColumns.split(' ').filter(Boolean).length || 1
    // Fractional height - offsetHeight rounding drifts page offsets on long lists
    const pitch = card.getBoundingClientRect().height + gap
    const rows = Math.max(1, Math.floor((frame.clientHeight + gap) / pitch))
    const totalRows = Math.ceil(grid.querySelectorAll('[data-product-id]').length / cols)
    const pages = Math.max(1, Math.ceil(totalRows / rows))
    const next = { pitch, rows, cols, viewH: rows * pitch - gap, pages, pad: (pages * rows - totalRows) * pitch }
    setGeo(prev => (
      Object.keys(next).every(k => prev[k] === next[k]) ? prev : next
    ))
  }, [])

  useLayoutEffect(() => {
    measure()
    const ro = new ResizeObserver(() => measure())
    if (frameRef.current) ro.observe(frameRef.current)
    if (gridRef.current) ro.observe(gridRef.current)
    return () => ro.disconnect()
  }, [measure])

  // New category / search → back to page 1
  useLayoutEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0
    measure()
  }, [filtered, measure])

  useEffect(() => { updatePosition() }, [geo, updatePosition])

  const goToPage = (delta) => {
    const el = scrollRef.current
    if (!el || !pageHeight) return
    // Snap points are page starts only, so the list always rests on a whole page
    const target = (Math.round(el.scrollTop / pageHeight) + delta) * pageHeight
    el.scrollTo({ top: Math.max(0, target), behavior: 'smooth' })
  }

  const lastScrollAt = useRef(0)
  const handleScroll = () => {
    lastScrollAt.current = Date.now()
    updatePosition()
  }

  const handlePointerDown = (e) => {
    if (!e.isPrimary) return
    pressRef.current = {
      x: e.clientX,
      y: e.clientY,
      t: Date.now(),
      top: scrollRef.current?.scrollTop || 0,
      // A tap that stops a moving list only stops it (like native lists)
      whileScrolling: Date.now() - lastScrollAt.current < 150
    }
  }

  // Tap adds the product; a swipe/drag of the list must not
  const handleItemClick = (e, product) => {
    const press = pressRef.current
    pressRef.current = null
    const fresh = press && Date.now() - press.t <= TAP_MAX_MS
    // keyboard / synthetic clicks have no coordinates - let them through
    const hasCoords = e.clientX !== 0 || e.clientY !== 0
    if (fresh && press.whileScrolling) return
    if (fresh && hasCoords) {
      const moved = Math.hypot(e.clientX - press.x, e.clientY - press.y)
      const scrolled = Math.abs((scrollRef.current?.scrollTop || 0) - press.top)
      if (moved > TAP_MAX_MOVE || scrolled > TAP_MAX_MOVE) return
    }
    onProductClick(product)
  }

  const handleItemKeyDown = (e, product) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onProductClick(product)
    }
  }

  return (
    <PagedGrid>
      <GridFrame ref={frameRef}>
        <GridViewport style={geo.viewH ? { height: geo.viewH } : undefined}>
          <GridScrollArea
            ref={scrollRef}
            onScroll={handleScroll}
            onPointerDown={handlePointerDown}
          >
            <GridWrapper ref={gridRef} style={geo.pad ? { paddingBottom: geo.pad } : undefined}>
              {filtered.map((product, index) => (
                <GridItem
                  key={product.id}
                  $pageStart={index % (geo.rows * geo.cols) === 0}
                  role="button"
                  tabIndex={0}
                  data-product-id={product.id}
                  onClick={e => handleItemClick(e, product)}
                  onKeyDown={e => handleItemKeyDown(e, product)}
                >
                  {product.icon ? (
                    <GridItemIcon>{product.icon}</GridItemIcon>
                  ) : (
                    <GridItemImage>
                      <img src={product.imageUrl} alt={productTitle(product, i18n.language)} />
                    </GridItemImage>
                  )}
                  <GridItemTitle>{productTitle(product, i18n.language)}</GridItemTitle>
                  <GridItemFooter>
                    <GridItemPrice>{formatPrice(product.price, currency)}</GridItemPrice>
                    <GridItemCode>{product.id}</GridItemCode>
                  </GridItemFooter>
                </GridItem>
              ))}
              {filtered.length === 0 && (
                <GridEmpty>{t('scanner.noProducts')}</GridEmpty>
              )}
            </GridWrapper>
          </GridScrollArea>
          <GridEdgeFade $edge="top" $visible={pos.canUp} />
          <GridEdgeFade $edge="bottom" $visible={pos.canDown} />
        </GridViewport>
      </GridFrame>

      {geo.pages > 1 && (
        <Pager>
          <PageButton
            type="button"
            data-pager="up"
            aria-label={t('scanner.prevPage')}
            disabled={!pos.canUp}
            onClick={() => goToPage(-1)}
          >
            <ChevronIcon up />
          </PageButton>
          <PageIndicator
            data-pager="indicator"
            role="status"
            aria-label={t('scanner.pageOf', { page: pos.page, pages: geo.pages })}
          >
            <strong>{pos.page}</strong>
            <span>/ {geo.pages}</span>
          </PageIndicator>
          <PageButton
            type="button"
            data-pager="down"
            aria-label={t('scanner.nextPage')}
            disabled={!pos.canDown}
            onClick={() => goToPage(1)}
          >
            <ChevronIcon />
          </PageButton>
        </Pager>
      )}
    </PagedGrid>
  )
}

export default ProductGrid
